"use client";

import { useState } from "react";
import { Image } from "@/components/ui/image";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import "@/styles/ticker.css";

const allTestimonials = [
  // 5 strongest first
  {
    name: "Sonja Song",
    company: "Airbnb",
    photo: "/images/sonja-square.jpg",
    quote: "This program has been truly transformative, helping me understand how HR can help AI-led organizational transformation.",
  },
  {
    name: "Daniel Kuyumcu",
    company: "Google",
    photo: "/images/daniel-kuyumcu-square.jpg",
    quote: "This was a highly valuable experience for anyone seeking to apply AI to more areas across HR.",
  },
  {
    name: "Matthew Rond",
    company: "IO Psychology",
    photo: "/images/matthew-rond-square.jpg",
    quote: "Helped me move from skeptic to champion, from uncertain to having frameworks for a path forward.",
  },
  {
    name: "Anestasia Talton, MBA",
    company: "Fractionals United",
    photo: "/images/anestasia.jpg",
    quote: "I developed the strategic confidence to ethically and responsibly integrate AI into HR practices.",
  },
  {
    name: "Nathalie Salles-Olivier",
    company: "PhD, Human-AI Collaboration",
    photo: "/images/nathalie-square.jpg",
    quote: "The magic is learning with people who get your world. Same challenges, same language.",
  },
  // Remaining testimonials
  {
    name: "Vince Carbone",
    company: "Eaglestone Equipment",
    photo: "/images/vince-square.jpg",
    quote: "Highly recommend this course for HR Leaders looking to expand their AI knowledge base.",
  },
  {
    name: "Montarious Usher",
    company: "Duracell Inc.",
    photo: "/images/montarious-square.jpg",
    quote: "I took a leap and joined the very first cohort of the AI for HR Mastermind course, and it was a great experience!",
  },
  {
    name: "Laurie Gartrell",
    company: "AgileOne",
    photo: "/images/laurie-square.jpg",
    quote: "Both Neil and Johnathan are great!! They are personable, smart and very responsive.",
  },
  {
    name: "Carrie Ott-Holland, PhD",
    company: "IO Psychologist",
    photo: "/images/carrie-square.jpg",
    quote: "It really encouraged me to think about AI adoption as a behavioral change process.",
  },
  {
    name: "Alexia",
    company: "AgAmerica",
    photo: "/images/1683813597077.jpeg",
    quote: "This course was a game-changer. GenAI isn't a magic wand, it's a thought partner.",
  },
  {
    name: "Ryan Belding",
    company: "Interface",
    photo: "/images/ryan-belding-square.jpg",
    quote: "Just to say thank you, as I found the experience to be invaluable!",
  },
  {
    name: "Sarah Okuma",
    company: "Ameriprise Financial",
    photo: "/images/sarah-okuma-square.jpg",
    quote: "Neil and Jonathan were really wonderful, knowledgeable, open to feedback, honest and engaging.",
  },
  {
    name: "Yolanda Hawkes",
    company: "The Hawkeye Group",
    photo: "/images/yolanda-hawkes-square.jpg",
    quote: "I appreciate the thorough and intentional approach. The class size was appropriate.",
  },
  {
    name: "Laura Baranowski, MS",
    company: "Toyota Connected NA",
    photo: "/images/laura-baranowski-square.jpg",
    quote: "I am so glad that I stepped out of my comfort zone to participate.",
  },
  {
    name: "Najdana Majors",
    company: "CoinTracker",
    photo: "/images/najdana-majors-square.jpg",
    quote: "It was great, I am very grateful for being an early participant.",
  },
];

type Testimonial = typeof allTestimonials[0];

const TickerCard = ({ t, onClick }: { t: Testimonial; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-4 glass rounded-xl px-5 py-4 mr-4 w-[360px] flex-shrink-0 text-left hover:ring-2 hover:ring-primary/40 transition-all cursor-pointer"
    aria-label={`Read full testimonial from ${t.name}`}
  >
    <Image
      src={t.photo}
      alt={t.name}
      width={48}
      height={48}
      className="w-12 h-12 rounded-full object-cover border-2 border-primary/30 flex-shrink-0"
    />
    <div className="min-w-0">
      <p className="text-sm italic text-foreground/80 leading-snug line-clamp-2">
        "{t.quote}"
      </p>
      <p className="text-xs font-semibold text-foreground mt-1">
        {t.name} <span className="font-normal text-muted-foreground">· {t.company}</span>
      </p>
    </div>
  </button>
);

export const TopTestimonialsSection = () => {
  const [active, setActive] = useState<Testimonial | null>(null);
  // Duplicate list for seamless loop
  const items = [...allTestimonials, ...allTestimonials];
  const isOpen = active !== null;

  return (
    <section className="py-3 md:py-4 bg-foreground/5 overflow-hidden">
      <div className="ticker-wrapper">
        <div className="ticker-track" data-paused={isOpen ? "true" : "false"}>
          {items.map((t, i) => (
            <TickerCard key={`${t.name}-${i}`} t={t} onClick={() => setActive(t)} />
          ))}
        </div>
      </div>

      <Dialog open={isOpen} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent className="max-w-lg">
          {active && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <Image
                    src={active.photo}
                    alt={active.name}
                    width={64}
                    height={64}
                    className="w-16 h-16 rounded-full object-cover border-2 border-primary/30 flex-shrink-0"
                  />
                  <div className="text-left">
                    <DialogTitle className="text-base">{active.name}</DialogTitle>
                    <DialogDescription className="text-sm">{active.company}</DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              <blockquote className="mt-4 text-base italic text-foreground/90 leading-relaxed border-l-4 border-primary/40 pl-4">
                "{active.quote}"
              </blockquote>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
